/**
 * Live progress for one processing run.
 *
 * Stages rather than a percentage, since the running stage is the diagnosis.
 * Two kinds of run come through here, normalising and classifying.
 */

import { useFollowRecordModule, useModule } from '../modules/useModule';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
	useFileEventsQuery,
	useFileQuery,
	useProcessingRunQuery,
} from '../../queries/intake';
import { PageHeader } from '../../provider/pageTitle';
import { useClientCrumbs } from './crumbs';
import { Card, ErrorNotice, Note, StatusBadge } from '../../shared/ui';
import StageProgress from '../../shared/components/StageProgress';
import type { StageStatus } from '../../shared/components/StageProgress/types';
import { ErrorState, LoadingState } from '../shared/states';
import { count, dateTime } from '../../utils/format';
import { Button, CircularProgress } from '@mui/material';

/** `python/app/application/normalize.py:STAGES`, held here so unreached stages can be
 *  drawn. */
const NORMALISE_STAGES = [
	{ key: 'download', label: 'Preparing' },
	{ key: 'normalise', label: 'Applying the column mapping' },
	{ key: 'upload', label: 'Saving the prepared rows' },
	{ key: 'verify', label: 'Checking' },
] as const;

const CLASSIFY_STAGES = [
	{ key: 'classify', label: 'Applying classification rules' },
] as const;

/** Which run this is, from the stages reported. A run that has reported
 *  nothing yet is treated as normalising. */
function stagesFor(steps: ReadonlyArray<{ stage: string }>) {
	const seen = new Set(steps.map((step) => step.stage));
	return CLASSIFY_STAGES.some((stage) => seen.has(stage.key)) &&
		seen.size <= 1
		? CLASSIFY_STAGES
		: NORMALISE_STAGES;
}

export default function FileProcessing() {
	const { clientId = '', fileId = '' } = useParams();
	const [params] = useSearchParams();
	const runId = params.get('run') ?? '';
	/* The file's module wins over the URL's, for the badge and every link. */
	const fileModule = useFileQuery(fileId || null).data?.module;
	useFollowRecordModule(fileModule);
	const clientCrumbs = useClientCrumbs(clientId, fileModule);
	const { link } = useModule();

	/* Polling stops itself once the run is no longer RUNNING or QUEUED. */
	const [active, setActive] = useState(true);
	const run = useProcessingRunQuery(runId, active);
	useEffect(() => {
		if (run.data)
			setActive(
				run.data.status === 'RUNNING' || run.data.status === 'QUEUED',
			);
	}, [run.data]);
	const view = run.data;
	/* An H2R file is never classified: only the normalise stages are shown and
     a finished run means the file is ready for audits. */
	const isH2R = fileModule === 'H2R';
	/* The dates and numbers that would not convert are recorded on the file's
     "Normalised" event (the run itself does not carry them). */
	const events = useFileEventsQuery(
		isH2R && run.data?.status === 'COMPLETED' ? fileId : null,
	);
	const unconverted = (() => {
		const normalised = (events.data ?? [])
			.filter((e) => e.event.startsWith('Normalised'))
			.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
		return /values that would not convert: ([^·]+)/
			.exec(normalised?.event ?? '')?.[1]
			?.trim();
	})();

	if (!runId) {
		return (
			<Note tone="warn" title="No run to watch">
				This screen follows one processing run and none was named. Open
				the file and start processing from there.
			</Note>
		);
	}
	if (run.isPending && !view) return <LoadingState label="Loading…" />;
	if (run.error && !view)
		return <ErrorState error={run.error} onRetry={run.refetch} />;
	if (!view) return null;

	const STAGES = isH2R ? NORMALISE_STAGES : stagesFor(view.steps);
	const byStage = new Map(view.steps.map((step) => [step.stage, step]));
	const failedStep = view.steps.find((step) => step.status === 'FAILED');
	const done = view.status === 'COMPLETED';
	const failed = view.status === 'FAILED';
	const split = view.classification;

	return (
		<div className="stack">
			<PageHeader
				breadcrumbs={[
					...clientCrumbs,
					{
						label: view.fileName,
						to: `/intake/clients/${clientId}/files/${fileId}`,
					},
				]}
				title="Processing"
				subtitle={
					<>
						{view.startedAt
							? `Started ${dateTime(view.startedAt)}`
							: 'Starting…'}
						{view.finishedAt
							? ` · finished ${dateTime(view.finishedAt)}`
							: ''}
					</>
				}
				actions={
					<span
						className="row"
						style={{ gap: 'var(--s2)', alignItems: 'center' }}
					>
						{!done && !failed && (
							<CircularProgress
								variant="determinate"
								value={view.progress}
								size={20}
								aria-label={`${view.progress}% done`}
							/>
						)}
						<StatusBadge group="runStatus" value={view.status} />
						{done && (
							<Button
								component={Link}
								variant="contained"
								size="small"
								to={link(
									`/intake/clients/${clientId}/files/${fileId}`,
								)}
							>
								Open the file
							</Button>
						)}
					</span>
				}
			/>

			<div className="pagebar">
				{active && (
					<span className="small mut">
						This page updates itself — nothing is lost if you leave
						and come back.
					</span>
				)}
			</div>

			<ErrorNotice error={run.error} />

			{isH2R && done && (
				<Note tone="ok" title="Ready for audits" row>
					The rows are processed. This file can now be used in an
					audit.
					{unconverted && (
						<>
							{' '}
							Some values could not be read and were left blank:{' '}
							{unconverted}.
						</>
					)}
				</Note>
			)}

			{failed && (
				<Note
					tone="danger"
					title={
						failedStep
							? `Failed while it was ${STAGES.find((s) => s.key === failedStep.stage)?.label.toLowerCase() ?? failedStep.stage}`
							: 'Processing failed'
					}
					row
				>
					{failedStep?.detail ??
						view.errorMessage ??
						'No message was recorded.'}
				</Note>
			)}

			<Card
				title="Progress"
				hint={
					active
						? 'Updates automatically. You can leave this page; processing continues.'
						: undefined
				}
			>
				<StageProgress
					stages={STAGES}
					statusOf={(key) =>
						(byStage.get(key)?.status ?? 'PENDING') as StageStatus
					}
					detailOf={(key) => byStage.get(key)?.detail ?? null}
				/>
			</Card>

			{/* Shown even when every bucket is zero: "no rule matched" is a result. */}
			{!isH2R && split.classifiedAt && (
				<Card
					title="How the rows were classified"
					hint={`${count(split.total)} rows · classified ${dateTime(split.classifiedAt)}`}
				>
					<div className="stack2">
						{[
							['Sales', split.sales],
							['Returns', split.returns],
							['Adjustments', split.adjustments],
							['Unclassified', split.unclassified],
						].map(([label, n]) => (
							<div
								key={String(label)}
								className="row spread up-item"
							>
								<span className={n === 0 ? 'mut' : ''}>
									{label}
								</span>
								<span
									className={
										label === 'Unclassified' &&
										Number(n) > 0
											? 'b warn'
											: 'b'
									}
								>
									{count(Number(n))}
								</span>
							</div>
						))}
					</div>
					{split.unclassified > 0 && (
						<Note
							tone="info"
							title="Unclassified rows are a real result"
							row
						>
							No published rule claimed them. They are loaded and
							queryable; re-apply the rules from the file page
							once the rules cover them.
						</Note>
					)}
				</Card>
			)}
		</div>
	);
}
